import React from "react";
import withStyles from "isomorphic-style-loader/withStyles";
import { connect } from "react-redux";
import { Row, Col, Button, FormGroup, Input, Label } from "reactstrap";
import ModalFrame from "../../../toolkit/modalFrame";
import s from "./styles.scss";
import { ScaleLoader } from "react-spinners";
import { resetPassword, adminOtpChallenge } from "../../../utils/fetch";
import AdminOtpPanel from "../../../toolkit/adminOtpPanel";
import classNames from "classnames";
import * as modalActions from "../../../utils/actions/modals";
import Reform from "@franleplant/reform";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { ensureAccessToken } from "../../../utils/firebaseToken";

interface Props extends ServiceCallerProps {
  userId: string;
  isOpen: boolean;
  toggle: () => void;
  firebase: any;

  toggleChangePasswordModal: () => void;
}

type DisplayState = "normal" | "success";

interface State {
  fields: {
    oldPassword: string;
    newPassword: string;
    newConfirmation: string;
  };
  loaded: boolean;
  loading: boolean;
  displayState: DisplayState;
  fetchMessage: string;
  otpChallengeId: string | null;
  passwordOtpActive: boolean;
  otpSmsEligible: boolean;
  otpDeliveryChannel: "email" | "sms";
}

class ChangePassword extends React.Component<Props, State> {
  re = Reform.reactMixins.objectMixin(this);

  closeModalTimer: ReturnType<typeof setTimeout> | null = null;

  state = {
    fields: {
      oldPassword: "",
      newPassword: "",
      newConfirmation: "",
    },
    errors: {},
    loaded: true,
    loading: false,
    displayState: "normal" as DisplayState,
    fetchMessage: null,
    otpChallengeId: null,
    passwordOtpActive: false,
    otpSmsEligible: false,
    otpDeliveryChannel: "email",
  };

  validationRules = {
    oldPassword: { required: true, minLength: 6 },
    newPassword: { required: true, minLength: 6 },
    newConfirmation: { required: true, minLength: 6 },
  };

  validationMessages = {
    required: _ => `Required`,
    minLength: ruleValue => `Must be at least ${ruleValue} characters long`,
    default: _ => `Invalid`,
  };

  UNSAFE_componentWillUpdate(nextProps) {
    if (this.props.isOpen && !nextProps.isOpen) {
      if (this.closeModalTimer) {
        clearTimeout(this.closeModalTimer);
        this.closeModalTimer = null;
      }
      this.setState({ loaded: false });
    }
  }

  componentWillUnmount() {
    if (this.closeModalTimer) {
      clearTimeout(this.closeModalTimer);
    }
  }

  componentDidUpdate() {
    if (!this.state.loaded && this.props.config) {
      this.setState({
        loaded: true,
        fetchMessage: null,
        displayState: "normal",
        fields: {
          oldPassword: "",
          newPassword: "",
          newConfirmation: "",
        },
        otpChallengeId: null,
        passwordOtpActive: false,
      });
    }
  }

  onChangeFactory = fieldName => {
    return event => {
      const value = event.target.value;
      this.setState(state => {
        const fields = {
          ...state.fields,
          [fieldName]: value,
        };
        return { ...state, error: {}, fields };
      });
      this.re.validateField(fieldName, value);
    };
  };

  keepCurrentSession(token?: string): Promise<void> {
    if (!token) {
      return Promise.resolve();
    }
    return firebase
      .auth()
      .signInWithCustomToken(token)
      .then(() => ensureAccessToken({ forceRefresh: true }))
      .then(
        () => undefined,
        () => undefined
      );
  }

  runPasswordChangeWithCompletionToken(completionToken: string, fromOtpFlow = false) {
    return resetPassword(
      this.props.config.authServiceUrl,
      this.props.serviceAccessToken,
      {
        oldPassword: this.state.fields.oldPassword,
        newPassword: this.state.fields.newPassword,
        completionToken,
      }
    )
      .then(response =>
        this.keepCurrentSession(response.token).then(() => response.message)
      )
      .then(response => {
        if (fromOtpFlow) {
          this.setState({
            loading: false,
            passwordOtpActive: false,
            otpChallengeId: null,
          });
          if (this.closeModalTimer) {
            clearTimeout(this.closeModalTimer);
          }
          this.closeModalTimer = window.setTimeout(() => {
            this.closeModalTimer = null;
            this.props.toggleChangePasswordModal();
          }, 900);
        } else {
          this.setState({
            loading: false,
            fetchMessage: response,
            displayState: "success",
            passwordOtpActive: false,
            otpChallengeId: null,
          });
        }
      })
      .catch(error => {
        this.setState({
          fetchMessage: fromOtpFlow ? null : error.message || String(error),
          loading: false,
        });
        return Promise.reject(error);
      });
  };

  onPasswordOtpVerified = (completionToken: string) => {
    this.setState({ loading: true, fetchMessage: null });
    return this.runPasswordChangeWithCompletionToken(completionToken, true);
  };

  onSubmit = e => {
    e.preventDefault();
    const isValid = this.re.validateFormFromState();
    if (!isValid) {
      return;
    }
    if (
      this.state.fields.newPassword !== this.state.fields.newConfirmation
    ) {
      return;
    }
    this.setState({
      loading: true,
      fetchMessage: null,
    });
    const tokenPromise = this.props.serviceAccessToken
      ? Promise.resolve(this.props.serviceAccessToken)
      : this.props.firebase.auth().currentUser
        ? this.props.firebase.auth().currentUser.getIdToken(false)
        : Promise.reject(new Error("Not signed in."));
    tokenPromise
      .then(token => {
        if (!token) {
          throw new Error("Not signed in.");
        }
        return adminOtpChallenge(this.props.config.authServiceUrl, {
          clientType: "web",
          operation: "change-password",
          firebaseToken: token,
        });
      })
      .then(ch => {
        if (!ch.otpRequired && ch.completionToken) {
          void this.runPasswordChangeWithCompletionToken(ch.completionToken, false);
          return;
        }
        if (ch.challengeId) {
          this.setState({
            loading: false,
            otpChallengeId: ch.challengeId,
            passwordOtpActive: true,
            otpSmsEligible: ch.deliveryChannel === "sms",
            otpDeliveryChannel: ch.deliveryChannel === "sms" ? "sms" : "email",
          });
          return;
        }
        throw new Error("Unexpected verification response.");
      })
      .catch(error => {
        this.setState({
          fetchMessage: error.message || String(error),
          loading: false,
        });
      });
  };

  backFromPasswordOtp = () => {
    this.setState({
      passwordOtpActive: false,
      otpChallengeId: null,
      otpSmsEligible: false,
      otpDeliveryChannel: "email",
      fetchMessage: null,
    });
  };

  render() {
    return (
      <ModalFrame
        title={`Change Password`}
        isOpen={this.props.isOpen}
        toggle={this.props.toggle}
      >
        <div className={s.centered}>
          <ScaleLoader color={"#1f65b1"} loading={!this.state.loaded} />
        </div>

        {this.state.loaded && (
          <div>
            {this.state.displayState === "normal" && (
              <div>
                {this.state.passwordOtpActive &&
                this.props.config?.authServiceUrl &&
                this.state.otpChallengeId ? (
                  <Row>
                    <Col xs={12}>
                      <AdminOtpPanel
                        authServiceUrl={this.props.config.authServiceUrl}
                        challengeId={this.state.otpChallengeId}
                        smsEligible={this.state.otpSmsEligible}
                        initialChannel={this.state.otpDeliveryChannel}
                        onVerified={this.onPasswordOtpVerified}
                        onBack={this.backFromPasswordOtp}
                        intro="Enter the code we sent to verify this password change."
                        showVerifiedSuccessScreen
                        verifiedSuccessTitle="Password successfully updated!"
                        verifiedSuccessBlurb="Please wait while we save your new password."
                      />
                    </Col>
                  </Row>
                ) : (
                  <>
                <Row>
                  <Col xs={12}>
                    <FormGroup>
                      <Label for="oldPassword" className={s.label}>
                        Current Password
                      </Label>
                      <Input
                        type="password"
                        name="oldPassword"
                        id="oldPassword"
                        value={this.state.fields.oldPassword}
                        onChange={this.onChangeFactory("oldPassword")}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul>
                      {this.re
                        .mapFieldErrors("oldPassword")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <FormGroup>
                      <Label for="New Password" className={s.label}>
                        New Password
                      </Label>
                      <Input
                        type="password"
                        name="New Password"
                        id="New Password"
                        value={this.state.fields.newPassword}
                        onChange={this.onChangeFactory("newPassword")}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul>
                      {this.re
                        .mapFieldErrors("newPassword")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>
                  </Col>
                </Row>
                <Row className={s.generalMargin}>
                  <Col xs={12}>
                    <FormGroup>
                      <Label for="newConfirmation" className={s.label}>
                        Confirm New Password
                      </Label>
                      <Input
                        type="password"
                        name="newConfirmation"
                        id="newConfirmation"
                        value={this.state.fields.newConfirmation}
                        onChange={this.onChangeFactory("newConfirmation")}
                        className={classNames(s.textField, s.roundedCorners)}
                      />
                    </FormGroup>
                    <ul>
                      {this.re
                        .mapFieldErrors("newConfirmation")
                        .map((message, index) => {
                          return (
                            <li key={index} className={s.error}>
                              {message}
                            </li>
                          );
                        })}
                    </ul>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className={s.centerAlign}>
                    {this.state.fetchMessage && (
                      <div className={s.fetchMessage}>
                        <span>{this.state.fetchMessage}</span>
                      </div>
                    )}
                    {this.state.fields.newPassword !==
                      this.state.fields.newConfirmation && (
                      <div className={s.passwordError}>
                        <span>Password must match</span>
                      </div>
                    )}
                    <Button
                      className={classNames(s.updateButton, s.bigButton)}
                      onClick={this.onSubmit}
                      disabled={
                        this.state.loading ||
                        this.state.fields.newPassword !==
                          this.state.fields.newConfirmation
                      }
                    >
                      {this.state.loading ? "Updating" : "Update"}
                    </Button>
                  </Col>
                </Row>
                  </>
                )}
              </div>
            )}
            {this.state.displayState === "success" && (
              <div>
                <Row>
                  <Col xs={12} className={s.centerAlign}>
                    {this.state.fetchMessage && (
                      <div className={s.fetchMessage}>
                        <span>{this.state.fetchMessage}</span>
                      </div>
                    )}
                    <Button
                      className={classNames(s.updateButton, s.bigButton)}
                      onClick={this.props.toggleChangePasswordModal}
                    >
                      Close
                    </Button>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        )}
      </ModalFrame>
    );
  }
}

const matchStateToProps = state => {
  const { user } = state;
  return {
    userName: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    photoUrl: user.photoUrl,
    id: user.id,
  };
};

const mapDispatchToProps = dispatch => ({
  toggleChangePasswordModal: () => {
    dispatch(modalActions.toggleChangePasswordModal());
  },
});

export default serviceCaller(
  connect<Props>(
    matchStateToProps,
    mapDispatchToProps
  )(withStyles(s)(ChangePassword))
);
